import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Loader2 } from "lucide-react";
import "@/App.css";

import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ProtectedRoute, AdminRoute } from "./components/ProtectedRoute";
import { initAnalyticsIfConsented, trackPageview } from "./lib/analytics";

import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import ChatWidget from "./components/ChatWidget";
import CookieConsent from "./components/CookieConsent";
import { ScrollToTop } from "./components/ScrollToTop";
import { Toaster } from "./components/ui/sonner";

// Kept as eager (non-lazy) imports: these are the site's primary Google-entry
// pages (brand searches + the 250+ programmatic SEO pages). Bundling them in
// the main chunk avoids an extra network round-trip on first paint (LCP).
import Home from "./pages/Home";
import NetworkPage from "./pages/NetworkPage";
import SEOLandingPage from "./pages/SEOLandingPage";

// Everything else is code-split (React.lazy): each page is fetched only when
// a visitor actually navigates to it, which keeps the initial JS bundle much
// smaller. The whole admin back-office (13 pages) becomes a separate bundle
// that public visitors never download at all.
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const PaymentPage = lazy(() => import("./pages/PaymentPage"));
const OrderConfirmation = lazy(() => import("./pages/OrderConfirmation"));
const OrderTracking = lazy(() => import("./pages/OrderTracking"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const FAQ = lazy(() => import("./pages/FAQ"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const LegalNoticePage = lazy(() => import("./pages/LegalNoticePage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminNetworks = lazy(() => import("./pages/admin/AdminNetworks"));
const AdminPromoCodes = lazy(() => import("./pages/admin/AdminPromoCodes"));
const AdminTranslations = lazy(() => import("./pages/admin/AdminTranslations"));
const AdminEmails = lazy(() => import("./pages/admin/AdminEmails"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
const AdminFaq = lazy(() => import("./pages/admin/AdminFaq"));
const AdminLogs = lazy(() => import("./pages/admin/AdminLogs"));
const AdminSupportTickets = lazy(() => import("./pages/admin/AdminSupportTickets"));
const AdminSuppliers = lazy(() => import("./pages/admin/AdminSuppliers"));
const AdminFinances = lazy(() => import("./pages/admin/AdminFinances"));

function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="route-loading-fallback">
      <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
    </div>
  );
}

function SiteLayout({ children }) {
  return (
    <>
      <Navbar />
      {children}
      <Footer />
      <ChatWidget />
      <CookieConsent />
    </>
  );
}

function AppRoutes() {
  const location = useLocation();

  useEffect(() => { initAnalyticsIfConsented(); }, []);
  useEffect(() => { trackPageview(location.pathname); }, [location.pathname]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/login" element={<SiteLayout><Login /></SiteLayout>} />
      <Route path="/register" element={<SiteLayout><Register /></SiteLayout>} />
      <Route path="/paiement/:orderNumber" element={<SiteLayout><PaymentPage /></SiteLayout>} />
      <Route path="/checkout/:productId" element={<SiteLayout><CheckoutPage /></SiteLayout>} />
      <Route path="/order-confirmation/:orderNumber" element={<SiteLayout><OrderConfirmation /></SiteLayout>} />
      <Route path="/suivi-commande" element={<SiteLayout><OrderTracking /></SiteLayout>} />
      <Route path="/reseaux/:slug" element={<SiteLayout><NetworkPage /></SiteLayout>} />
      <Route path="/blog" element={<SiteLayout><Blog /></SiteLayout>} />
      <Route path="/blog/:slug" element={<SiteLayout><BlogPost /></SiteLayout>} />
      <Route path="/faq" element={<SiteLayout><FAQ /></SiteLayout>} />
      <Route path="/cgv" element={<SiteLayout><TermsPage /></SiteLayout>} />
      <Route path="/politique-de-confidentialite" element={<SiteLayout><PrivacyPage /></SiteLayout>} />
      <Route path="/mentions-legales" element={<SiteLayout><LegalNoticePage /></SiteLayout>} />
      <Route path="/contact" element={<SiteLayout><ContactPage /></SiteLayout>} />

      <Route path="/dashboard" element={<SiteLayout><ProtectedRoute><Dashboard /></ProtectedRoute></SiteLayout>} />

      <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="networks" element={<AdminNetworks />} />
        <Route path="promo-codes" element={<AdminPromoCodes />} />
        <Route path="translations" element={<AdminTranslations />} />
        <Route path="emails" element={<AdminEmails />} />
        <Route path="blog" element={<AdminBlog />} />
        <Route path="reviews" element={<AdminReviews />} />
        <Route path="faq" element={<AdminFaq />} />
        <Route path="logs" element={<AdminLogs />} />
        <Route path="support-tickets" element={<AdminSupportTickets />} />
        <Route path="suppliers" element={<AdminSuppliers />} />
        <Route path="finances" element={<AdminFinances />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>

      <Route path="/" element={<SiteLayout><Home /></SiteLayout>} />
      <Route path="/:slug" element={<SiteLayout><SEOLandingPage /></SiteLayout>} />
      <Route path="*" element={<SiteLayout><NotFound /></SiteLayout>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App font-body">
      <HelmetProvider>
        <BrowserRouter>
          <ThemeProvider>
            <LanguageProvider>
              <CurrencyProvider>
                <AuthProvider>
                  <ScrollToTop />
                  <Suspense fallback={<RouteFallback />}>
                    <AppRoutes />
                  </Suspense>
                  <Toaster />
                </AuthProvider>
              </CurrencyProvider>
            </LanguageProvider>
          </ThemeProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
